import { useEffect, useMemo } from 'react';
import getSclaApp from 'features/scla/api/getSclaApp';
import useSclaApplicationYears from 'features/scla/hooks/useSclaApplicationYears';
import useUserStore from 'store/useUserStore';
import {
  resolveSclaApplication,
  sclaApplicationStatusLabel,
  type SclaApplicationStatusLabel,
} from 'utils/sclaApplicationKind';

// Account already checked this session, so users without an application
// are not re-fetched every time the nav mounts.
let checkedUserId: string | null = null;

export default function useSclaApplicationStatus(
  userId: string | undefined,
  userType: string | undefined,
): { status: SclaApplicationStatusLabel; cohortYear: string } | null {
  const { data } = useSclaApplicationYears();
  const application = useUserStore(s => s.application);
  const { allowed } = resolveSclaApplication(userType);
  const cohortYear = data?.currentCohortYear ?? null;
  const currentApplication =
    application?.cohortYear === cohortYear ? application : null;
  const hasCurrentApplication = Boolean(currentApplication);
  const statusLabel = sclaApplicationStatusLabel(currentApplication);

  useEffect(() => {
    if (!allowed || !userId || !cohortYear || checkedUserId === userId) {
      return undefined;
    }
    if (hasCurrentApplication) {
      checkedUserId = userId;
      return undefined;
    }

    let mounted = true;
    getSclaApp(cohortYear)
      .then(app => {
        if (!mounted) return;
        checkedUserId = userId;
        if (app) useUserStore.getState().setUserApplication(app);
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
    };
  }, [allowed, userId, cohortYear, hasCurrentApplication]);

  return useMemo(() => {
    if (!allowed || !cohortYear) return null;
    return { status: statusLabel, cohortYear };
  }, [allowed, cohortYear, statusLabel]);
}
